<script setup>
import { useStore } from "vuex";
import { useLoading } from 'vue-loading-overlay';
import { computed, onMounted, ref, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useRouter } from "vue-router";
import { prettifyDateTime, copyToClipBoard, successNotify } from "@/mixins";
import { usePageHeader } from "@/composables/usePageHeader";

const route = useRoute();
const router = useRouter();
const store = useStore();
const $loading = useLoading({});
const { setPageTitle, setPageCrumbs } = usePageHeader();

const orderId = computed(() => route.params.id);
const orderData = computed(() => store.getters['order/orderData']);
const getOrderError = computed(() => store.getters['order/getOrderError']);

const showImageModal = ref(false);
const selectedImage = ref('');

const getOrder = (id) => store.dispatch('order/getOrder', id);

const shortId = computed(() => (orderData.value?.id ? `#${String(orderData.value.id).substring(0, 8)}` : ''));

function loadOrder(id) {
  if (!id) return;
  const loader = $loading.show({});
  getOrder(id)
    .then(() => {
      if (orderData.value?.id) {
        setPageTitle(shortId.value);
        setPageCrumbs(['Operations', 'Rental Orders', shortId.value]);
      }
    })
    .finally(() => loader.hide());
}

onMounted(() => {
  loadOrder(orderId.value);
  document.addEventListener('keydown', handleKeydown);
});

// Refetch when navigating between order detail pages (component is reused)
watch(orderId, (id) => loadOrder(id));

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

function handleKeydown(event) {
  if (event.key === 'Escape' && showImageModal.value) {
    closeImageModal();
  }
}

function formatDateTime(dateTime) {
  if (!dateTime) return '—';
  return prettifyDateTime(dateTime);
}

function goBack() {
  router.push('/orders');
}

const copiedId = ref(false);
function copyOrderId() {
  if (orderData.value?.id) {
    copyToClipBoard(orderData.value.id.toString());
    successNotify('Order ID copied');
    copiedId.value = true;
    setTimeout(() => { copiedId.value = false; }, 1200);
  }
}

const EVENT_META = {
  drop_off_token_used: { label: 'Drop-off token used', icon: 'qrcode-scan', sev: 'info' },
  pick_up_token_used: { label: 'Pick-up token used', icon: 'qrcode-scan', sev: 'info' },
  cell_was_opened: { label: 'Cell was opened', icon: 'lock-open-variant-outline', sev: 'warning' },
  cell_was_closed: { label: 'Cell was closed', icon: 'lock-outline', sev: 'success' },
  order_locked: { label: 'Order locked', icon: 'lock-check-outline', sev: 'info' },
  cell_photo_was_received: { label: 'Cell photo received', icon: 'camera-outline', sev: 'info' },
};

const eventMeta = (type) => EVENT_META[type] || { label: type, icon: 'circle-medium', sev: 'info' };

const STATUS_PILL = { created: 'created', active: 'active', finished: 'finished', expired: 'expired' };

function getFullImageUrl(photoUrl) {
  if (!photoUrl) return '';

  // Already an absolute URL — use as is, don't prepend a base
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;

  // Get base URL using the same logic as in session.js
  let baseURL;
  if (process.env.VUE_APP_API_BASE_URL) {
    baseURL = process.env.VUE_APP_API_BASE_URL;
  } else if (window.origin.includes('dev') || window.origin.includes('localhost')) {
    baseURL = 'https://dev.api-lockerbot.com/';
  } else {
    baseURL = 'https://api-lockerbot.com/';
  }

  return `${baseURL}${photoUrl}`;
}

function openImageModal(imageUrl) {
  selectedImage.value = imageUrl;
  showImageModal.value = true;
}

function closeImageModal() {
  showImageModal.value = false;
  selectedImage.value = '';
}
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-ghost" type="button" @click="goBack">
      <span class="mdi mdi-arrow-left"></span>
      Back to Orders
    </button>
  </Teleport>

  <div v-if="getOrderError" class="card table-empty">
    Failed to load order details. Please try again.
  </div>

  <template v-else-if="orderData && Object.keys(orderData).length > 0">
    <!-- header band -->
    <div class="lkd-detail">
      <div class="lkd-dhead">
        <div class="ic"><span class="mdi mdi-clipboard-text-outline"></span></div>
        <div>
          <div class="kind">Rental Order</div>
          <div class="odt-titlerow">
            <h2 class="mono">{{ shortId }}</h2>
            <button
              class="lkd-copybtn"
              :class="{ done: copiedId }"
              type="button"
              title="Copy Order ID"
              @click="copyOrderId">
              <span class="mdi" :class="copiedId ? 'mdi-check' : 'mdi-content-copy'"></span>
            </button>
          </div>
        </div>
        <div class="odt-dhead-actions" style="margin-left: auto; display: flex; align-items: center; gap: 14px;">
          <span class="pill" :class="`pill--${STATUS_PILL[String(orderData.status).toLowerCase()] || 'created'}`">
            <span class="d"></span>
            {{ orderData.status }}
          </span>
        </div>
      </div>

      <div class="odt-split" style="padding: 26px;">
        <!-- Order details -->
        <div>
          <div class="emp-head" style="padding: 0 0 10px;">
            <span class="mdi mdi-clipboard-text-outline" style="color: var(--lb-blue); font-size: 19px;"></span>
            <h3>Order Details</h3>
          </div>
          <dl class="lkd-dl">
            <dt>Price</dt>
            <dd>{{ orderData.price || '—' }}</dd>
            <dt>Organization</dt>
            <dd>{{ orderData.organization || '—' }}</dd>
            <dt>Created</dt>
            <dd>{{ formatDateTime(orderData.created_at) }}</dd>
            <dt>Locked</dt>
            <dd>{{ formatDateTime(orderData.locked_at) }}</dd>
            <dt>Expires</dt>
            <dd>{{ formatDateTime(orderData.expired_at) }}</dd>
            <dt>Finished</dt>
            <dd>{{ formatDateTime(orderData.finished_at) }}</dd>
          </dl>
        </div>
        <!-- Device information -->
        <div>
          <div class="emp-head" style="padding: 0 0 10px;">
            <span class="mdi mdi-locker" style="color: var(--lb-blue); font-size: 19px;"></span>
            <h3>Device Information</h3>
          </div>
          <dl class="lkd-dl">
            <dt>Device</dt>
            <dd>{{ orderData.device || '—' }}</dd>
            <dt>Cell number</dt>
            <dd class="mono">{{ orderData.cell_number ?? '—' }}</dd>
            <dt>Last updated</dt>
            <dd>{{ formatDateTime(orderData.updated_at) }}</dd>
          </dl>
        </div>
      </div>
    </div>

    <!-- Events timeline -->
    <div class="card">
      <div class="card__head">
        <h2>Events Timeline</h2>
        <div class="sub">{{ orderData.events_log?.events?.length || 0 }} events</div>
      </div>
      <div v-if="orderData.events_log?.events?.length" class="odt-timeline">
        <div v-for="(event, index) in orderData.events_log.events" :key="index" class="odt-item">
          <div class="odt-rail">
            <div class="dot" :class="eventMeta(event.type).sev">
              <span class="mdi" :class="`mdi-${eventMeta(event.type).icon}`"></span>
            </div>
            <div class="line"></div>
          </div>
          <div class="odt-main">
            <div class="odt-top">
              <b>{{ eventMeta(event.type).label }}</b>
              <span class="time">{{ formatDateTime(event.datetime) }}</span>
            </div>
            <div v-if="event.detail" class="odt-detail">{{ event.detail }}</div>
            <div v-if="event.photo_url" class="odt-photo">
              <img
                :src="getFullImageUrl(event.photo_url)"
                alt="Cell photo"
                @click="openImageModal(getFullImageUrl(event.photo_url))" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="odt-empty">
        <span class="mdi mdi-timeline-text-outline"></span>
        No events recorded for this order yet.
      </div>
    </div>
  </template>

  <div v-else class="card table-empty">Loading order details…</div>

  <!-- fullscreen photo viewer -->
  <Teleport to="body">
    <div v-if="showImageModal" class="odt-imgview" @click="closeImageModal">
      <button class="close" type="button" title="Close" @click="closeImageModal">
        <span class="mdi mdi-close"></span>
      </button>
      <img :src="selectedImage" alt="Cell photo" @click.stop />
    </div>
  </Teleport>
</template>
